import { clsx, type ClassValue } from "clsx";
import { format } from "date-fns";
import { Customer } from "src/redux/types/_customer.type";
import { twMerge } from "tailwind-merge";
import { subscriptionPlans } from "./SubscriptionPlans";
import { NavigateOptions } from "react-router-dom";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const FormatString = (str) => {
  return str
    .split("_") // Split string on underscores
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize first letter of each word
    .join(" "); // Join the words with spaces
};

export const convertTimeToIST = (time: string) => {
  const options: Intl.DateTimeFormatOptions = {
    timeZone: "Asia/Kolkata",
    hour: "numeric",
    minute: "numeric",
  };
  const formatter = new Intl.DateTimeFormat("en-IN", options);
  return formatter.format(new Date(time));
};

export const calculateReportingTime = (startTime: string) => {
  const startDateTime = new Date(startTime);

  // Subtract 45 minutes (45 * 60 * 1000 milliseconds) from the start time
  const reportingTime = new Date(startDateTime.getTime() - 45 * 60 * 1000);

  return convertTimeToIST(reportingTime.toISOString());
};

export const calculateLastEntryTime = (startTime: string) => {
  const startDateTime = new Date(startTime);

  // Subtract 10 minutes (10 * 60 * 1000 milliseconds) from the start time
  const reportingTime = new Date(startDateTime.getTime() - 10 * 60 * 1000);

  return convertTimeToIST(reportingTime.toISOString());
};

export const formatDate = (isoDateString: string) => {
  const date = new Date(isoDateString);

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();

  return `${month}-${day}-${year}`;
};

export const formatDateDDMM = (isoDateString: string) => {
  const date = new Date(isoDateString);

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

export const formatDateMMDD = (date: string): string => {
  const [mm, dd, yyyy] = date.split("-");
  return `${mm}-${dd}-${yyyy}`;
};

export const FORMATDATEMMDD = (date: string) => {
  const dateArray = date.split("-");
  const yy = dateArray[0];
  const mm = dateArray[1];
  const dd = dateArray[2];

  return `${mm}-${dd}-${yy}`;
};

export const formatDateYYYYMMDD = (date: string | Date) => {
  return format(new Date(date), "yyyy-MM-dd");
};

type SetSearchParams = (
  update: (prev: URLSearchParams) => URLSearchParams | Record<string, string>,
  navigateOpts?: NavigateOptions,
) => void;

export const updateSearchParams1 = (
  key: string,
  value: string,
  setSearchParams: SetSearchParams,
) => {
  setSearchParams(
    (prev) => {
      const next = Object.fromEntries(prev);

      if (value.trim() === "") {
        delete next[key];
      } else {
        next[key] = value;
      }

      next.page = "1";

      return next;
    },
    { replace: true },
  );
};

export const updateSearchParams = (
  key: string,
  value: string,
  setSearchParams: Function,
  searchParams: URLSearchParams,
) => {
  setSearchParams(
    {
      ...Object.fromEntries(searchParams),
      [key]: value,
    },
    { replace: true },
  );
};

export function calculateHoursPresent(
  checkin_time: string | null,
  checkout_time: string | null,
): number {
  if (!checkin_time || !checkout_time) {
    return 0;
  }

  const checkIn = new Date(checkin_time);
  const checkOut = new Date(checkout_time);

  const diffMs = checkOut.getTime() - checkIn.getTime(); // difference in milliseconds

  if (diffMs <= 0) {
    return 0;
  }

  const diffHours = diffMs / (1000 * 60 * 60); // convert ms → hours

  return parseFloat(diffHours.toFixed(2)); // round to 2 decimal places
}

export const transformForApi = (values) => {
  return {
    states: values.stateDistricts.map((s) => s.state),
    districts: values.stateDistricts.flatMap((s) => s.districts),
  };
};

export const joinByComma = (
  items: (string | null | undefined)[],
  separator: string = ", ",
): string => {
  return items
    .filter((item): item is string => !!item && item.trim() !== "")
    .join(separator);
};

export const stringToArray = (input): string[] => {
  if (!input) return [];

  return input
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item !== "");
};

export function fullName(c?: Customer) {
  if (!c) return "—";
  return [c.first_name, c.middle_name, c.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
}

export function initials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join("");
}

export const validTill = (duration: number, started: string) => {
  const date = new Date(started);
  const currentDate = date.getDate();
  const currentMonth = date.getMonth();
  const currentYear = date.getFullYear();

  if (duration === 3) {
    return format(
      new Date(currentYear, currentMonth + 3, currentDate),
      "dd MMM yyyy",
    );
  } else {
    return format(
      new Date(currentYear + 1, currentMonth, currentDate),
      "dd MMM yyyy",
    );
  }
};

export const getTitle = (duration: number, member: number) => {
  return subscriptionPlans.find(
    (sub) => sub.duration === duration && sub.members === member,
  )?.title;
};

export const calculateAge = (dob: string) => {
  if (!dob) return null;

  const [year, month, day] = dob.split("-").map(Number);

  if (!year || !month || !day) return null;

  const today = new Date();

  let age = today.getFullYear() - year;

  const birthdayPassed =
    today.getMonth() + 1 > month ||
    (today.getMonth() + 1 === month && today.getDate() >= day);

  if (!birthdayPassed) {
    age--;
  }

  return age;
};